import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { authFetch, getAuthHeaders } from '../services/auth';

// Admin orders slice. The old catalog half (categories/analytes/packages) was
// removed together with the storefront — lab packages live in the inventory
// section and talk to /api/admin/lab/* directly.
export interface AdminOrder {
  orderId: string;
  invoiceId: string;
  status: string;
  paymentMethod: string;
  orderDate: string;
  customer: {
    customerId: string;
    email: string;
    name: string;
  };
  totalAmount: {
    amount: number;
    currency: string;
  };
  itemCount: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOrdersResponse {
  orders: AdminOrder[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface AdminStore {
  orders: AdminOrder[];
  ordersResponse: AdminOrdersResponse | null;
  loading: boolean;
  error: string | null;

  fetchOrders: (page?: number, pageSize?: number, status?: string, search?: string) => Promise<void>;
  updateOrderStatus: (orderId: string, status: string) => Promise<void>;
  clearError: () => void;
}

// Single source of truth for authorized requests (401 -> refresh -> redirect).
const adminFetch = (path: string, options: RequestInit = {}): Promise<Response> =>
  authFetch(path, options);

export const useAdminStore = create<AdminStore>()(
  devtools(
    (set, get) => ({
      orders: [],
      ordersResponse: null,
      loading: false,
      error: null,

      fetchOrders: async (page = 1, pageSize = 20, status?: string, search?: string) => {
        set({ loading: true, error: null });
        try {
          const queryParams = new URLSearchParams();
          queryParams.append('page', page.toString());
          queryParams.append('pageSize', pageSize.toString());

          if (status) {
            queryParams.append('status', status);
          }

          if (search?.trim()) {
            queryParams.append('search', search.trim());
          }

          const response = await adminFetch(`/api/admin/orders?${queryParams}`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const ordersResponse: AdminOrdersResponse = await response.json();
          set({
            orders: ordersResponse.orders,
            ordersResponse,
            loading: false
          });
        } catch (error) {
          set({ error: (error as Error).message, loading: false });
        }
      },

      updateOrderStatus: async (orderId: string, status: string) => {
        set({ loading: true, error: null });
        try {
          const response = await adminFetch(`/api/admin/orders/${orderId}/status`, {
            method: 'PUT',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
          }

          const { orders } = get();
          const updatedOrders = orders.map(order =>
            order.orderId === orderId
              ? { ...order, status, updatedAt: new Date().toISOString() }
              : order
          );

          set({ orders: updatedOrders, loading: false });
        } catch (error) {
          set({ error: (error as Error).message, loading: false });
          throw error;
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: 'admin-store' }
  )
);
